import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Rendert Markdown (Dokument-Inhalte, Chat-Antworten) sicher + lesbar.
 * react-markdown escaped HTML per Default (kein dangerouslySetInnerHTML nötig).
 *
 * `compact`: straffe Block-Abstände für Chat-Bubbles. Ohne diese Overrides bringt die
 * `prose`-Typografie großzügige Artikel-Margins mit (zwischen Überschrift/Absatz/Liste),
 * was in einer Chat-Antwort als riesige Lücken erscheint.
 *
 * `renderQuelle`: macht die Belege „[Quelle 3]" im Fließtext anklickbar. Ohne diese Prop
 * bleiben sie einfacher Text (so verhalten sich Dokument-Ansicht und Ticket-Panel weiter).
 * Umsetzung: Vor dem Rendern wird „[Quelle 3]" zu einem Markdown-Link mit dem Schema
 * `#pc-quelle-3` umgeschrieben; der `a`-Override fängt genau dieses Schema ab und übergibt
 * die Nummer an `renderQuelle`. So bleibt die Markdown-Verarbeitung unangetastet und der
 * Beleg landet zuverlässig als eigenes Element im Textfluss.
 */
const QUELLE_RE = /\[Quelle\s+(\d+)\]/g;
const QUELLE_HREF = /^#pc-quelle-(\d+)$/;

export default function MarkdownContent({
  text,
  compact,
  renderQuelle,
}: {
  text: string;
  compact?: boolean;
  renderQuelle?: (nr: number) => React.ReactNode;
}) {
  const spacing = compact
    ? ' leading-snug prose-p:my-1 prose-headings:mt-2 prose-headings:mb-1 ' +
      'prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:marker:text-ps-dark-400 ' +
      'prose-hr:my-2 first:prose-headings:mt-0'
    : '';

  const quellenText = renderQuelle
    ? (text || '').replace(QUELLE_RE, (_m, nr) => `[${nr}](#pc-quelle-${nr})`)
    : text || '';

  const components = renderQuelle
    ? {
        // react-markdown erwartet hier ein Element (kein ReactNode) -> immer eines liefern
        a({ href, children, ...props }: any): JSX.Element {
          const treffer = typeof href === 'string' ? href.match(QUELLE_HREF) : null;
          if (treffer) {
            const gerendert = renderQuelle(Number(treffer[1]));
            // Nummer unbekannt (LLM hat eine Quelle erfunden) -> unauffällig als Text lassen
            return gerendert
              ? <>{gerendert}</>
              : <span className="text-ps-dark-400">[{treffer[1]}]</span>;
          }
          return <a href={href} {...props}>{children}</a>;
        },
      }
    : undefined;

  return (
    <div className={'prose prose-sm max-w-none text-ps-dark-700 ' +
                    'prose-headings:text-ps-dark-700 prose-a:text-ps-dark-600 ' +
                    'prose-code:text-ps-dark-700 prose-pre:bg-gray-50 prose-pre:text-ps-dark-700' +
                    spacing}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {quellenText}
      </ReactMarkdown>
    </div>
  );
}
